import type { HelpContent } from "../../../shared/help/types";

export const HELP_CONTENT: HelpContent = {
  app: "auth",
  title: "Academy Access help",
  intro:
    "Find the right entry route, recover account access, and understand application and onboarding requirements.",
  support:
    "Use the Academy or institution contact that issued your invitation for access questions. Prepare a summary with the relevant application or invitation reference, never a password or verification code.",
  guides: [
    {
      id: "entry",
      title: "Choose the right way into the Academy",
      category: "Getting started",
      areas: ["welcome", "signin", "signup", "workspaces"],
      summary: "Use your existing account, invitation or application route.",
      before:
        "Check whether you already have an Academy account or an invitation from an institution or organization.",
      steps: [
        "If you already have an account, use Sign in with its registered email.",
        "If you received an invitation, use the original invitation link so the assigned role and scope can be checked.",
        "If you are new, use Create account and choose the route matching your learning, family, teaching or institutional purpose.",
        "Complete required verification before choosing a workspace. The requesting app may limit which roles are valid for that entry.",
      ],
      outcome:
        "You reach the next account or application step for your intended role without creating a duplicate identity.",
      problems: [
        {
          question: "The role I want is not available",
          answer:
            "Privileged roles can require an invitation, review or institutional assignment. Ask the relevant workspace owner; signing up does not grant every role.",
        },
        {
          question: "I opened the wrong role app",
          answer:
            "Return to the intended app or original invitation and start its sign-in flow. Do not change callback or role parameters to bypass the requested scope.",
        },
      ],
      actions: [
        {
          label: "Open Sign in",
          area: "screen",
          page: "signin",
        },
        {
          label: "Use an invitation",
          area: "screen",
          page: "invite",
        },
        {
          label: "Open role guide",
          area: "modal",
          page: "role-guide",
        },
        {
          label: "Learn for myself",
          area: "application",
          page: "learner-self",
        },
        {
          label: "Apply for a child",
          area: "application",
          page: "parent-child",
        },
        {
          label: "Apply to teach",
          area: "application",
          page: "teaching",
        },
        {
          label: "Apply as a proctor",
          area: "application",
          page: "proctor",
        },
        {
          label: "Enrol an organization",
          area: "application",
          page: "organization",
        },
        {
          label: "Enrol an institution",
          area: "application",
          page: "institution",
        },
      ],
    },
    {
      id: "password",
      title: "Recover access to your account",
      category: "Account access",
      areas: ["signin", "forgot-password", "reset-password"],
      summary: "Use the password recovery path for your existing email.",
      before: "Use the email associated with your Academy account.",
      steps: [
        "From Sign in choose Forgot password and enter the registered email.",
        "Follow the recovery instructions shown by the form. This preview simulates the flow and does not send a real recovery email.",
        "On the reset step, enter and confirm a password that meets the displayed requirements.",
        "Return to Sign in and check that you are using the intended account and requesting workspace.",
      ],
      outcome:
        "The recovery flow completes its local validation. A preview reset does not change credentials in a live identity provider.",
      problems: [
        {
          question: "No email arrived",
          answer:
            "Email delivery is not connected in this preview. In a live service, check the address and spam folder and use the established account-support contact.",
        },
        {
          question: "The form rejects the password",
          answer:
            "Read the displayed requirements and ensure the confirmation matches. Never put the password into a support message.",
        },
      ],
      actions: [
        {
          label: "Open password recovery",
          area: "screen",
          page: "forgot-password",
        },
      ],
    },
    {
      id: "verification",
      title: "Understand email verification and MFA",
      category: "Verification",
      areas: ["verify-email", "mfa"],
      summary: "Complete the correct verification step without sharing security codes.",
      before: "Keep the account email and requesting role consistent throughout sign-in.",
      steps: [
        "Read whether the screen asks for email verification or multi-factor authentication; they are separate checks.",
        "Enter the code for the displayed step, respecting any format or expiry message.",
        "Use the resend or recovery option shown by the screen if the real code is unavailable.",
        "For this frontend preview only, email verification uses 482731 and MFA uses 246810. These are test values, not live security credentials.",
      ],
      outcome:
        "You progress past the relevant local verification step; privileged access may still require an assigned role or review.",
      problems: [
        {
          question: "My code is rejected",
          answer:
            "Check that you are on the correct email or MFA step and remove extra spaces. In a live flow use the latest code for that account.",
        },
        {
          question: "I cannot use my MFA device",
          answer:
            "Use the institution’s established account recovery process. Do not ask another user to share their verification code.",
        },
      ],
      actions: [],
    },
    {
      id: "invitation",
      title: "Accept an invitation for the correct role",
      category: "Invitations",
      areas: ["invite"],
      summary: "Check the email, role and workspace carried by the invitation.",
      before: "Use the original invitation sent to your own email address.",
      steps: [
        "Open the invitation entry and inspect the requesting workspace or role.",
        "Enter the invitation information requested by the form and verify the identity it is intended for.",
        "Complete account verification and any role-specific requirement shown after acceptance.",
        "In this preview only, ACADEMY-INVITE-2026 is the demonstration invitation code; it does not represent a live issued invitation.",
      ],
      outcome:
        "The local flow prepares the intended role and scope; acceptance does not bypass review or MFA requirements.",
      problems: [
        {
          question: "The invitation is for another email",
          answer: "Ask the issuer to correct or reissue it. Do not sign in as another person.",
        },
        {
          question: "The invitation is invalid or expired",
          answer:
            "Use the original link and ask the issuing institution or organization to confirm its status. A manually selected role does not replace an invitation.",
        },
      ],
      actions: [
        {
          label: "Open invitation entry",
          area: "screen",
          page: "invite",
        },
      ],
    },
    {
      id: "application",
      title: "Complete an application without losing your place",
      category: "Applications",
      areas: ["application"],
      summary: "Work through the steps that apply to your chosen route.",
      before: "Have accurate identity, program preferences and required documents ready.",
      steps: [
        "Review the selected application route and role; later steps depend on that choice.",
        "Complete the current step, reading required-field errors and any eligibility or consent conditions before continuing.",
        "For program, track or cohort choices, compare prerequisites, schedule, language and available capacity.",
        "Review the complete application before submitting and keep an exported or separate copy of important information.",
      ],
      outcome:
        "Required fields are complete and the review screen reflects your intended route and choices. Local submission is not a live admissions receipt.",
      problems: [
        {
          question: "A step disappeared or changed",
          answer:
            "Different routes require different steps. Check the selected route before re-entering information.",
        },
        {
          question: "I reopened the app and cannot find my draft",
          answer:
            "Drafts in this preview are browser-local. Use the same browser and profile; clearing site data can remove them. Keep a separate copy of documents and references.",
        },
      ],
      actions: [],
    },
    {
      id: "status",
      title: "Understand application review and an offer",
      category: "Applications",
      areas: ["status", "offer", "checkout"],
      summary: "Distinguish submission, human review, an offer and funding readiness.",
      before: "Have the application reference and the displayed status available.",
      steps: [
        "Read the status and any requested information or review dependency.",
        "If more information is needed, prepare the specific documents or corrections requested.",
        "For an offer, review program, cohort, conditions and acceptance requirements before accepting.",
        "Check the funding route and any invoice or checkout requirement with the actual admissions or billing contact before making a real payment.",
      ],
      outcome:
        "You know the next condition to meet and the office responsible. Preview review decisions, offers and payments are simulations.",
      problems: [
        {
          question: "The application is pending",
          answer:
            "A pending state requires the indicated review; opening another role or workspace does not approve it.",
        },
        {
          question: "The checkout shows a receipt",
          answer:
            "The preview does not process a real charge. Confirm actual payment only through the institution’s established billing channel.",
        },
      ],
      actions: [],
    },
    {
      id: "onboarding",
      title: "Finish onboarding and open your workspace",
      category: "Onboarding",
      areas: ["onboarding", "complete", "workspaces"],
      summary: "Complete role-specific readiness before entering the assigned app.",
      before: "Use the verified account and accepted role or offer for this journey.",
      steps: [
        "Read each onboarding item for your role, including policies, readiness and relevant orientation.",
        "Complete required acknowledgements and check any remaining restriction.",
        "Choose the assigned workspace only when the application shows it is ready.",
        "If the destination returns you to sign-in, start again from that app and report the exact message without copying authorization parameters.",
      ],
      outcome:
        "The destination matches the intended role and workspace; local onboarding does not replace live institutional approval.",
      problems: [
        {
          question: "The workspace is disabled",
          answer:
            "Inspect the displayed dependency: verification, MFA, application review or onboarding may still be incomplete.",
        },
        {
          question: "The sign-in return is rejected",
          answer:
            "Use the intended app’s normal entry URL again. Provide the app name and error message to support, not the full callback URL or security values.",
        },
      ],
      actions: [],
    },
    {
      id: "family",
      title: "Apply as a parent or guardian",
      category: "Family applications",
      areas: ["application"],
      summary: "Keep guardian identity and learner identity separate.",
      before: "Be ready to explain your relationship to the learner and provide only required information.",
      steps: [
        "Choose the parent or child application route rather than entering the learner’s identity as your own account.",
        "Complete the guardian relationship and learner details requested by that route.",
        "Read optional permissions separately from required processing and record your intended choice.",
        "Check age-related requirements and any relationship verification before expecting family workspace access.",
      ],
      outcome: "The application distinguishes the guardian, learner and requested permission scope.",
      problems: [
        {
          question: "I cannot access all of the learner’s work",
          answer:
            "Family access is limited by verified relationship, age, purpose and permissions. It does not automatically include private messages or assessment answers.",
        },
        {
          question: "The relationship needs review",
          answer:
            "Contact the institution responsible for verifying family access. Do not create a second account under the learner’s name.",
        },
      ],
      actions: [
        {
          label: "Apply for a child",
          area: "application",
          page: "parent-child",
        },
      ],
      pages: ["guardian", "consent", "learner"],
    },
  ],
};
